<script setup>
import { ref, computed, watch } from 'vue';

// Logo de uma integração, com recuo para quando o PNG não existe.
//
// Os arquivos vivem em public/dashboard/images/integrations e nem toda
// integração tem o seu: os conectores de compra (asaas, pagar.me, hotmart)
// nasceram sem imagem e o card mostrava o ícone quebrado do navegador, que
// parece defeito do produto. Em vez de um PNG por conector novo, o recuo é a
// inicial do nome sobre um fundo neutro do tema, então qualquer integração
// futura entra apresentável no dia em que aparece na lista.
const props = defineProps({
  integrationId: { type: [String, Number], default: '' },
  name: { type: String, default: '' },
  size: { type: String, default: 'md' }, // 'md' (48px) | 'lg' (64px)
});

const erroClaro = ref(false);
const erroEscuro = ref(false);

// Trocar de integração sem remontar o componente precisa dar nova chance às
// imagens; sem isso o card herdaria o erro do anterior.
watch(
  () => props.integrationId,
  () => {
    erroClaro.value = false;
    erroEscuro.value = false;
  }
);

const semImagem = computed(
  () => !props.integrationId || (erroClaro.value && erroEscuro.value)
);

// Falta só a variante de um tema é comum (o -dark costuma ser esquecido). Nesse
// caso a variante que existe cobre os dois temas, em vez de deixar um buraco.
const srcClaro = computed(() =>
  erroClaro.value
    ? `/dashboard/images/integrations/${props.integrationId}-dark.png`
    : `/dashboard/images/integrations/${props.integrationId}.png`
);
const srcEscuro = computed(() =>
  erroEscuro.value
    ? `/dashboard/images/integrations/${props.integrationId}.png`
    : `/dashboard/images/integrations/${props.integrationId}-dark.png`
);

const inicial = computed(() => {
  const texto = (props.name || String(props.integrationId) || '').trim();
  return texto ? texto.charAt(0).toLocaleUpperCase() : '?';
});

const classeCaixa = computed(() =>
  props.size === 'lg' ? 'size-16 text-2xl' : 'size-12 text-lg'
);
</script>

<template>
  <div
    class="flex items-center justify-center flex-shrink-0"
    :class="classeCaixa"
  >
    <div
      v-if="semImagem"
      class="flex items-center justify-center w-full h-full font-bold rounded-md select-none bg-n-alpha-2 text-n-slate-11 border border-n-weak"
      aria-hidden="true"
    >
      {{ inicial }}
    </div>
    <template v-else>
      <img
        :src="srcClaro"
        :alt="name"
        class="max-w-full rounded-md border border-n-weak block dark:hidden bg-n-alpha-3 dark:bg-n-alpha-2"
        @error="erroClaro = true"
      />
      <img
        :src="srcEscuro"
        :alt="name"
        class="max-w-full rounded-md border border-n-weak hidden dark:block bg-n-alpha-3 dark:bg-n-alpha-2"
        @error="erroEscuro = true"
      />
    </template>
  </div>
</template>
